<template>
  <v-menu v-model="menu" eager class="filter-menu" left offset-x :close-on-content-click="false">
    <template v-slot:activator="{ on }">
      <v-badge color="marca-1" :value="filtersQuantity" :content="filtersQuantity" overlap>
        <mf-button v-if="activatorType === 'icon'" color="cinza-0" type="icon" data-cy="filter" icon="filter_list" v-on:click.native="on.click" />
        <mf-button v-else text color="marca-0-l40" data-cy="filter" icon="filter_list" label="Filtros" v-on:click.native="on.click" />
      </v-badge>
    </template>

    <v-card width="285px">
      <v-card-text>
        <h3>Filtros</h3>
        <v-container fluid class="pa-0">
          <v-row v-for="(filter, index) in filters" :key="index">
            <v-col v-if="filter === 'clube'">
              <label>Clube</label>
              <select-clube v-model="filterFidelidade" @input="filterChange" />
            </v-col>
            <v-col v-if="filter === 'categorias'">
              <label>Categorias</label>
              <select-categories-modal ref="categoriesModal" v-model="filterCategories" @input="filterChange" />
            </v-col>
            <v-col v-if="filter === 'campanhas'">
              <label>Campanhas</label>
              <select-campaigns-modal
                ref="campaignsModal"
                v-model="filterCampaigns"
                :mode="['sms', 'email', 'push', 'whatsapp']"
                include-maria-campaign
                hide-offer-sales
                :status="campaignsFiltersStatus"
                :use-campaign-frequency="useCampaignFrequency"
                @input="filterChange"
              />
            </v-col>
            <v-col v-else-if="filter === 'lojas'">
              <label>Lojas</label>
              <mf-select-units-dialog
                ref="mfSelectUnitsDialog"
                hide-details
                :selected-units.sync="filterLojas"
                :selected-units-configuration.sync="filterUnitsConfiguration"
                :check-all-units-on-init="checkAllUnitsOnInit"
                :required="true"
                :units="units"
                :tag-list="tagList"
                :group-list="unitsGroups"
                :hide-ecommerce-configuration="hideEcommerceConfiguration"
                :filter-to-units="filterToUnits"
                enable-inactive-units-selection
                @change="confirmSelectUnits($event)"
              />
            </v-col>
            <v-col v-else-if="filter === 'cliente'">
              <label>Cliente</label>
              <select-cliente v-model="filterCliente" @input="filterChange" />
            </v-col>
            <v-col v-else-if="filter === 'pessoa'">
              <label>Pessoa</label>
              <select-pessoa v-model="filterPessoa" @input="filterChange" />
            </v-col>
            <v-col v-else-if="filter === 'data'">
              <label>Data</label>
              <data-select
                ref="dataSelect"
                v-model="filterDate"
                :max-filter-days="maxFilterDays"
                :use-current-date-as-last-day="useCurrentDateAsLastDay"
                :default-date="defaultDate"
                @input="filterChange"
              />
            </v-col>
            <v-col v-else-if="filter === 'periodo'">
              <label>Período</label>
              <select-periodo v-model="filterPeriodo" @input="filterChange" />
            </v-col>
            <v-col v-else-if="filter === 'segmentos'">
              <label>Segmentos</label>
              <segmentos-dialog
                :required="false"
                single-line
                outlined
                :selecionados.sync="selectedSegments"
                template-type="input"
                call-warmup-segments
                @change="filterChange"
              />
            </v-col>
            <v-col v-else-if="filter === 'produtos'">
              <label>Produtos</label>
              <modal-graphql-search-content :selected-items.sync="selectedProducts" @input="filterChange" />
              <v-checkbox v-if="filters.includes('sem-produtos')" v-model="noProductsFilter" label="Sem produtos" @change="filterChange"></v-checkbox>
            </v-col>
            <v-col v-else-if="filter === 'canais'">
              <label class="d-flex flex-row align-center">
                Canais
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <span v-on="on"><mf-icon small class="ml-1">info</mf-icon></span>
                  </template>
                  <span>É obrigatório ter pelo menos 1 canal ativo.</span>
                </v-tooltip>
              </label>

              <checkbox-channels :selected.sync="channelsFilter" :channels="allowedChannels" @change="filterChange" />
            </v-col>
            <v-col v-else-if="filter === 'couponStatus'">
              <select-coupon-status :selected.sync="couponStatusFilter" @change="filterChange" />
            </v-col>
            <v-col v-else-if="filter === 'measurementUnits'">
              <v-select v-model="measureUnitFilter" label="Unidade de medida" :items="measureUnits" outlined item-text="name" item-value="measureAcronym" />
            </v-col>
          </v-row>
        </v-container>

        <mf-action-buttons
          :primary-button="{
            text: primaryButtonText,
            action: primaryButtonAction,
            isVisible: true,
            isDisabled: false,
          }"
          :cancel-button="{
            text: 'Limpar',
            action: clearAndRefetch,
            isVisible: true,
            isDisabled: false,
          }"
        />
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
import Vue from 'vue'
import DataSelect from '@/components/DataSelect.vue'
import { mapActions, mapState, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import store from '@/store'
import vuetify from '@/plugins/vuetify'

export default {
  components: {
    SelectClube: () => import('@/components/SelectClube.vue'),
    SelectPessoa: () => import('@/components/SelectPessoa.vue'),
    SelectCliente: () => import('@/components/SelectCliente.vue'),
    SelectPeriodo: () => import('@/components/SelectPeriodo'),
    SegmentosDialog: () => import('@/modules/campanhas/views/components/SegmentosDialog.vue'),
    SelectCouponStatus: () => import('@/components/SelectCouponStatus.vue'),
    DataSelect,
    ModalGraphqlSearchContent: () => import('@/components/ModalGraphqlSearchContent.vue'),
    CheckboxChannels: () => import('@/components/CheckboxChannels'),
    SelectCampaignsModal: () => import('@/components/select-campaigns-modal/SelectCampaignsModal.vue'),
    SelectCategoriesModal: () => import('@/components/select-categories-modal/SelectCategoriesModal.vue'),
  },
  props: {
    filters: Array,
    defaultDate: String,
    maxFilterDays: {
      type: Number,
      default: null,
    },
    useCurrentDateAsLastDay: {
      type: Boolean,
      default: false,
    },
    noClearUnitsWhenInit: {
      type: Boolean,
      required: false,
    },
    checkAllUnitsOnInit: {
      type: Boolean,
      default: true,
    },
    setCampaignsDateRange: {
      type: Boolean,
      default: false,
    },
    hideEcommerceConfiguration: {
      type: Boolean,
      default: false,
    },
    campaignsFiltersStatus: {
      type: Array,
      default: () => ['ACTIVE', 'FINISHED'],
    },
    filterToUnits: {
      type: Array,
      default: () => [],
    },
    activatorType: {
      type: String,
      default: 'icon',
      validator: (value) => ['icon', 'button'].includes(value),
    },
    useCampaignFrequency: {
      type: Boolean,
      default: false,
    },
    useFilterButton: {
      type: Boolean,
      default: false,
    },
    skipMountSideEffects: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dateInstance: null,
    menu: false,
    localSelectedSegments: [],
    localSelectedProducts: [],
    isPreFilter: false,
    localFilters: {
      units: [],
      segments: [],
      segmentsObjects: [],
      productsObjects: [],
      unitsConfiguration: [],
      data: undefined,
      cliente: '',
      pessoa: '',
      fidelidade: '',
      departments: null,
      periodo: '',
      products: [],
      noProducts: false,
      channels: null,
      campaigns: [],
      measureUnit: '',
      couponStatus: ['RECOVERED', 'NOT_RECOVERED', 'NOT_PROCESSED'],
    },
    measures: [],
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    ...mapGetters({ quickAction: 'getQuickAction', preFilters: 'getPreFilters' }),
    ...mapGetters(['units', 'tagList', 'measureUnits', 'unitsGroups']),
    filtersComputed: {
      get() {
        return this.useFilterButton ? this.localFilters : this.stateFilters
      },
      set(value) {
        if (this.useFilterButton) {
          this.localFilters = value
        } else {
          this.setFilters(value)
        }
      },
    },
    primaryButtonText() {
      return this.useFilterButton ? 'Filtrar' : 'Fechar'
    },
    disabledFilter() {
      return Boolean(this.datePicker && (!this.primeiraData || !this.segundaData))
    },
    selectedSegments: {
      // This local computed is used to only pass segment ids and full objects to the state filters
      // but to keep all the data required for the segments selector to work
      get() {
        return this.localSelectedSegments
      },
      set(value) {
        this.localSelectedSegments = value
        this.filterSegmentos = value.map((segment) => segment._id)
        this.filterSegmentosObjetos = value
      },
    },
    selectedProducts: {
      // This local computed is used to only pass products SKUs to the state filters
      // but to keep all the data required for the products selector to work
      get() {
        return this.localSelectedProducts
      },
      set(value) {
        this.localSelectedProducts = value
        this.productsFilter = value.map((product) => product.sku)
        this.productsObjectsFilter = value
      },
    },
    filterDate: {
      get() {
        return this.filtersComputed.data
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, data: value }
      },
    },
    filterCategories: {
      get() {
        return this.filtersComputed.categories
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, categories: value }
      },
    },
    filterCampaigns: {
      get() {
        return this.filtersComputed.campaigns
      },
      set(value) {
        if (this.setCampaignsDateRange && value.length > 0) {
          this.$refs.dataSelect[0].setSelectedPeriodAsCustom()
          this.filterDate = this.getCampaignsDateRange(value)
          this.$refs.dataSelect[0].setInitialAndFinalDate({ initial: this.filterDate[0], final: this.filterDate[1] })
        }
        this.filtersComputed = { ...this.filtersComputed, campaigns: value }
      },
    },
    filterLojas: {
      get() {
        return this.filtersComputed.units
      },
      set(value) {
        if (!value || value?.length <= 0) return
        this.filtersComputed = { ...this.filtersComputed, units: value }
      },
    },
    filterUnitsConfiguration: {
      get() {
        return this.filtersComputed.unitsConfiguration
      },
      set(value) {
        if (!value || value?.length <= 0) return
        this.filtersComputed = { ...this.filtersComputed, unitsConfiguration: value }
      },
    },
    filterSegmentos: {
      get() {
        return this.filtersComputed.segments
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, segments: value }
      },
    },
    filterSegmentosObjetos: {
      get() {
        return this.filtersComputed.segmentsObjects
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, segmentsObjects: value }
      },
    },
    filterCliente: {
      get() {
        return this.filtersComputed.cliente
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, cliente: value }
      },
    },
    filterPessoa: {
      get() {
        return this.filtersComputed.pessoa
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, pessoa: value }
      },
    },
    filterFidelidade: {
      get() {
        return this.filtersComputed.fidelidade
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, fidelidade: value }
      },
    },
    filterPeriodo: {
      get() {
        return this.filtersComputed.periodo
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, periodo: value }
      },
    },
    productsFilter: {
      get() {
        return this.filtersComputed.products
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, products: value }
      },
    },
    productsObjectsFilter: {
      get() {
        return this.filtersComputed.productsObjects
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, productsObjects: value }
      },
    },
    noProductsFilter: {
      get() {
        return this.filtersComputed.noProducts
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, noProducts: value }
      },
    },
    channelsFilter: {
      get() {
        return this.filtersComputed.channels
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, channels: value }
      },
    },
    couponStatusFilter: {
      get() {
        return this.filtersComputed.couponStatus
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, couponStatus: value }
      },
    },
    measureUnitFilter: {
      get() {
        return this.filtersComputed.measureUnit
      },
      set(value) {
        this.filtersComputed = { ...this.filtersComputed, measureUnit: value }
      },
    },
    allowedChannels() {
      const channels = [
        {
          label: 'WhatsApp',
          value: 'WHATSAPP',
          permissionName: 'campanhas/whatsapp',
          moduleName: 'whatsapp',
        },
        {
          label: 'SMS',
          value: 'SMS',
          permissionName: 'campanhas/sms',
        },
        {
          label: 'Aplicativo',
          value: 'PUSH',
          permissionName: 'campanhas/aplicativo',
          moduleName: 'push',
        },
        {
          label: 'E-mail',
          value: 'EMAIL',
          permissionName: 'campanhas/email-v2',
          moduleName: 'email_v2',
        },
      ]

      return channels.filter((channel) => this.checkPageAndModulePermission(channel.permissionName, channel?.moduleName))
    },
    allowedChannelsValues() {
      return this.allowedChannels.map((channel) => channel.value)
    },
    paramFilters() {
      const paramFilters = {
        pessoa: this.checkFilterAll(this.filterPessoa),
        clube: this.checkFilterAll(this.filterFidelidade),
        cliente: this.checkFilterAll(this.filterCliente),
        segments: this.checkFilterAll(this.filterSegmentos?.length ? this.filterSegmentos : null),
        segmentsObjects: this.checkFilterAll(this.filterSegmentosObjetos?.length ? this.filterSegmentosObjetos : null),
        units: this.checkFilterAll(this.filterLojas && this.filterLojas.length === this.units.length ? null : this.filterLojas),
        unitsConfiguration: this.checkFilterAll(
          this.filterUnitsConfiguration && this.filterUnitsConfiguration.length === this.units.length ? null : this.filterUnitsConfiguration
        ),
        data: this.checkFilterAll(this.filterDate, true),
        period: this.checkFilterAll(this.filterPeriodo === 'Mensal' ? null : this.filterPeriodo),
        products: this.checkFilterAll(this.productsFilter?.length ? this.productsFilter : null),
        noProducts: this.checkFilterAll(this.noProductsFilter || null),
        channels: this.checkFilterAll(this.channelsFilter?.length ? this.channelsFilter : null),
        couponStatus: this.checkFilterAll(this.couponStatusFilter?.length ? this.couponStatusFilter : null),
        measureUnit: this.checkFilterAll(this.measureUnitFilter),
      }
      for (let index in paramFilters) {
        if (paramFilters[index] === null || paramFilters[index] === 'null' || paramFilters[index].length === 0) delete paramFilters[index]
      }
      return paramFilters
    },
    filtersQuantity() {
      let quantity = Object.keys(this.paramFilters).length
      // segments and SegmentsObjects are in fact the same filters, so it should not be counted twice
      if (this.paramFilters.segments && this.paramFilters.segmentsObjects) {
        quantity -= 1
      }
      // units and unitsConfiguration also are the same
      if (this.paramFilters.units && this.paramFilters.unitsConfiguration) {
        quantity -= 1
      }
      // channels is considered if is not the default one
      if (this.paramFilters.channels?.length === this.allowedChannels?.length) {
        quantity -= 1
      }

      // coupons filters is considered if is not the default one
      if (this.paramFilters.couponStatus?.length === 3) {
        quantity -= 1
      }
      return quantity
    },
  },
  watch: {
    $route: {
      handler() {
        const quickAction = Boolean(this.$route.query.quick_action)
        if (quickAction) {
          this.prebuildQuickAction()
        }

        const isPrefilter = Boolean(this.$route.query.is_prefilter)
        if (isPrefilter) {
          this.prebuildPreFilters()
        }
        if (quickAction || isPrefilter) {
          this.$bus.$emit('filter')
        }
      },
      deep: true,
    },
  },
  async mounted() {
    // Skip side effects when used in embedded contexts
    if (this.skipMountSideEffects) {
      if (!this.measureUnits.length && this.filters.includes('measurementUnits')) await this.getMeasuresUnits()
      return
    }

    // Caso o filtro tenha uma data inicial diferente do padrão, é necessário que o componente
    // filtre com a data correta, antes de tudo.
    // Isso não estava acontecendo, porque o menu só é criado quando é aberto
    // Por causa disso será necessário 'montar' o componente via código,
    // para que a data seja filtrada corretamente.
    const ComponentClass = Vue.extend(DataSelect)
    this.dateInstance = new ComponentClass({
      store,
      vuetify,
      propsData: {
        maxFilterDays: this.maxFilterDays,
        useCurrentDateAsLastDay: this.useCurrentDateAsLastDay,
        defaultDate: this.defaultDate,
      },
    })
    this.dateInstance.$mount()
    this.clear()
    this.prebuildQuickAction()
    this.prebuildPreFilters()

    if (!this.measureUnits.length && this.filters.includes('measurementUnits')) await this.getMeasuresUnits()
    if (this.useFilterButton) {
      this.primaryButtonAction()
    } else {
      this.$bus.$emit('filter')
    }
  },
  methods: {
    ...mapActions(['setFilters', 'setIsIncludingSegments', 'unsetQuickAction', 'getMeasuresUnits', 'unsetPreFilters']),
    filterChange() {
      if (!this.useFilterButton) this.putFiltersOnQueryParam()
    },
    primaryButtonAction() {
      if (this.useFilterButton) {
        this.setFilters(this.filtersComputed)
        this.putFiltersOnQueryParam()
      } else {
        this.menu = false
      }
    },
    confirmSelectUnits(isAllUnitsSelected) {
      this.$emit('confirm-select-units', isAllUnitsSelected)
      this.putFiltersOnQueryParam()
    },
    putFiltersOnQueryParam() {
      // Unset quick actions when changing the filter
      this.setIsIncludingSegments(false)

      if (this.disabledFilter) return
      this.$nextTick(() => {
        this.$bus.$emit('filter')
      })
    },
    clear({ forceClear = false } = {}) {
      // When the user clicks the clear button, the selectedPeriod(state) of the DataSelect component is not updated, so this is a temporary solution for that, but it's a workaround and should be improved.
      if (this.$refs?.dataSelect && this.$refs?.dataSelect.length) this.$refs.dataSelect[0].setDefaultSelectedPeriod()

      this.dateInstance.selectDates(this.defaultDate)
      this.filterDate = [this.dateInstance.initialDate, this.dateInstance.finalDate]
      this.filterCliente = ''
      this.filterPessoa = ''
      this.measureUnitFilter = ''
      this.filterFidelidade = ''
      this.filterPeriodo = 'Mensal'
      this.selectedSegments = []
      this.selectedProducts = []
      this.noProductsFilter = false
      this.channelsFilter = this.allowedChannelsValues
      this.couponStatusFilter = ['RECOVERED', 'NOT_RECOVERED', 'NOT_PROCESSED']
      this.filterCampaigns = []
      this.filterCategories = []

      this.$nextTick(() => {
        if ((!this.isPreFilter || forceClear) && this.$refs.mfSelectUnitsDialog) {
          this.$refs.mfSelectUnitsDialog?.[0].clearFilters()
        }
        if ((!this.isPreFilter || forceClear) && this.$refs.campaignsModal) {
          this.$refs.campaignsModal?.[0].clearCampaigns()
        }
        if ((!this.isPreFilter || forceClear) && this.$refs.categoriesModal) {
          this.$refs.categoriesModal?.[0].clearCategories()
        }
      })
    },
    clearAndRefetch() {
      this.clear({ forceClear: true })
      this.$emit('confirm-select-units', true)
      this.$nextTick(() => {
        this.putFiltersOnQueryParam()
      })
    },
    checkFilterAll(filter, dateException) {
      if (filter === null || filter === '' || filter === undefined || dateException) return null
      return filter
    },
    checkPageAndModulePermission(pageName, moduleName) {
      const havePagePermission = this.$permissions.havePagePermission(pageName)

      if (moduleName) {
        const haveModulePermission = this.$getAllowedModules()[moduleName]
        return havePagePermission && haveModulePermission
      }

      return havePagePermission
    },
    async prebuildQuickAction() {
      if (!this.getIsQuickAction() || !this.quickAction?.segmentation) {
        return
      }
      this.selectedSegments = [this.quickAction.segmentation.segment]
      this.unsetQuickAction()
    },
    async prebuildPreFilters() {
      this.isPreFilter = this.getIsPreFilter()
      if (!this.isPreFilter) return
      if (!this.preFilters) return

      if (this.preFilters.segmentsObjects?.length) this.localSelectedSegments = this.preFilters.segmentsObjects
      if (this.preFilters.segments?.length) this.filterSegmentos = this.preFilters.segments
      if (this.preFilters.productsObjects?.length) this.selectedProducts = this.preFilters.productsObjects
      if (this.preFilters.cliente) this.filterCliente = this.preFilters.cliente
      if (this.preFilters.pessoa) this.filterPessoa = this.preFilters.pessoa
      if (this.preFilters.fidelidade) this.filterFidelidade = this.preFilters.fidelidade
      if (this.preFilters.units?.length) this.filterLojas = this.preFilters.units
      if (this.preFilters.unitsConfiguration?.length) this.filterUnitsConfiguration = this.preFilters.unitsConfiguration
      this.unsetPreFilters()
    },
    getCampaignsDateRange(campaigns) {
      if (campaigns.length === 1) return [campaigns[0].initial_date, campaigns[0].final_date]
      const oldestCampaign = campaigns.reduce((a, b) => (DateTime.fromISO(a.initial_date) < DateTime.fromISO(b.initial_date) ? a : b))
      const newestCampaign = campaigns.reduce((a, b) => (DateTime.fromISO(a.final_date) > DateTime.fromISO(b.final_date) ? a : b))

      return [oldestCampaign.initial_date, newestCampaign.final_date]
    },
  },
}
</script>
<style lang="scss">
.filter-menu {
  .v-menu__content {
    z-index: 5 !important;
  }
}
</style>
